import { API_ENDPOINTS } from "../../shared/config";

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: number;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: Response
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Centralized API client with automatic authentication handling
 */
export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || "";
  }

  /**
   * Make an authenticated API request
   */
  async request<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const fullUrl = url.startsWith("http") ? url : `${this.baseUrl}${url}`;
    
    const defaultOptions: RequestInit = {
      credentials: "include", // Include cookies for authentication
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(fullUrl, mergedOptions);

      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        window.location.href = "/login";
        throw new ApiError("Unauthorized", 401, response);
      }

      // Handle other HTTP errors
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = (await response.json()) as { error?: string };
          errorMessage = errorData?.error || errorMessage;
        } catch (_e) {
          // If we can't parse the error response, use the status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new ApiError(errorMessage, response.status, response);
      }

      // Parse JSON response
      const data = await response.json();
      return data as ApiResponse<T>;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Handle network errors
      throw new ApiError(
        error instanceof Error ? error.message : "Network error",
        0
      );
    }
  }

  /**
   * GET request
   */
  async get<T = any>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...options, method: "GET" });
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    body?: any,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      ...options,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    body?: any,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
      ...options,
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(url, { ...options, method: "DELETE" });
  }
}

// Create default API client instance
export const api = new ApiClient();

// Convenience functions for common API calls
export const authApi = {
  /**
   * Verify current authentication status
   */
  verify: () => api.get(API_ENDPOINTS.auth.verify),

  /**
   * Logout user
   */
  logout: () => api.post(API_ENDPOINTS.auth.logout),
};

export const workflowApi = {
  /**
   * Get list of workflows
   */
  list: (page = 1, limit = 20) =>
    api.get(`${API_ENDPOINTS.workflows}?page=${page}&limit=${limit}`),

  /**
   * Create a new workflow
   */
  create: (data: {
    name: string;
    description: string;
    objective: string;
    config?: any;
  }) => api.post(API_ENDPOINTS.createWorkflow, data),

  /**
   * Get workflow details
   */
  get: (id: string) => api.get(API_ENDPOINTS.getWorkflow(id)),

  /**
   * Start a workflow
   */
  start: (id: string) => api.post(API_ENDPOINTS.startWorkflow(id)),

  /**
   * Stop a workflow
   */
  stop: (id: string) => api.post(API_ENDPOINTS.stopWorkflow(id)),

  /**
   * Get workflow events
   */
  getEvents: (id: string) => api.get(API_ENDPOINTS.getWorkflowEvents(id)),
  
  /**
   * Update a workflow
   */
  update: (id: string, data: {
    name?: string;
    objective?: string;
    description?: string;
  }) => api.put(API_ENDPOINTS.getWorkflow(id), data),

  /**
   * Delete all workflows for current user
   */
  deleteAll: () => api.delete(API_ENDPOINTS.workflows),
};

export const agentApi = {
  /**
   * Get agents for a workflow
   */
  listForWorkflow: (workflowId: string) =>
    api.get(API_ENDPOINTS.listWorkflowAgents(workflowId)),

  /**
   * Update an agent (e.g., objective)
   */
  updateForWorkflow: (
    workflowId: string,
    agentId: string,
    data: Partial<{ objective: string; prompt: string }>,
  ) => api.put(API_ENDPOINTS.updateWorkflowAgent(workflowId, agentId), data),
};

// Export the main API client and convenience functions
export default api;
